import React from "react";
import { Link } from "react-router-dom";
import FamilyArchive from "../components/FamilyArchive";

export default function About() {
  return (
    <div data-testid="about-page">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-7 order-2 md:order-1">
          <p className="label mb-4">About</p>
          <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
            Joseph Graves Jr.
          </h1>
          <p className="serif italic text-2xl md:text-3xl text-[hsl(var(--ink-soft))] mt-6">
            Painter, educator, and community builder based in Denver, Colorado.
          </p>
        </div>
        <figure className="md:col-span-5 order-1 md:order-2">
          <div className="aspect-[4/5] overflow-hidden bg-[hsl(var(--surface))]">
            <img
              src="https://customer-assets.emergentagent.com/job_black-western-studio/artifacts/n4rwv6qh_DJI_20260119_113247_139.JPG"
              alt="Joseph Graves Jr. beside his Denver mural"
              className="w-full h-full object-cover"
            />
          </div>
        </figure>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-4">
            <p className="label">Artist Statement</p>
          </div>
          <div className="md:col-span-8 space-y-6 text-lg leading-relaxed text-[hsl(var(--ink-soft))]">
            <p>
              <span className="text-foreground">I paint to make people visible.</span>{" "}
              The figures in my work — Black cowboys, family, friends, the people
              I grew up around — carry stories that the American canon has either
              forgotten or refused to tell. My job is simply to give them a wall.
            </p>
            <p>
              I grew up in Kansas, studied at CU Boulder, and spent years in the
              legal field — including work alongside former Colorado Governor
              John Hickenlooper — before returning to the studio in 2016. My
              father was an artist; he stopped painting when I was nine. Years
              later I caught myself telling my daughter the same thing he had
              told me — "life just gets in the way" — and I decided that
              wouldn't be my story.
            </p>
            <p>
              The work draws on Mark Rothko, Jacob Lawrence, and Basquiat — I
              like to say I'm the remix. I sketch loose, defy the lines, and
              let the music and the open window dictate the painting. My
              practice spans figurative work, Black Western imagery, and
              abstract contemporary — but the throughline is the same: who gets
              remembered, who gets a frame, who gets the light.
            </p>
            <p className="serif italic text-xl text-foreground">
              "Get messy and defy the lines." — JG Jr.
            </p>
          </div>
        </div>
      </section>

      <FamilyArchive />

      <section className="border-t border-border bg-[hsl(var(--surface))]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
          <div className="md:col-span-4">
            <p className="label">Practice</p>
            <h2 className="serif text-3xl md:text-4xl mt-3 tracking-tight">
              Beyond the canvas.
            </h2>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
            <Block title="Education" body="Private and small-group lessons for youth and adults; workshops at schools, libraries, and community centers across Denver." />
            <Block title="Live Painting" body="On-site painting and live portraiture for galas, weddings, museum activations, and corporate events." />
            <Block title="Programming" body="Daddy Joe's Cowboy Capers — a youth program connecting Western heritage, craft, and storytelling." />
            <Block title="Studio Visits" body="Available by appointment for serious collectors, curators, and press." />
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
        <p className="label mb-4">Begin a conversation</p>
        <h2 className="serif text-4xl md:text-5xl tracking-tight font-light">
          Visit the studio.
        </h2>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link
            to="/studio-visit"
            className="bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em]"
            data-testid="about-cta-studio-visit"
          >
            Visit the studio
          </Link>
          <Link
            to="/commissions"
            className="border border-foreground px-8 py-4 text-xs uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors"
            data-testid="about-cta-commission"
          >
            Commission a piece
          </Link>
          <Link
            to="/contact"
            className="border border-foreground px-8 py-4 text-xs uppercase tracking-[0.18em] hover:bg-foreground hover:text-background transition-colors"
            data-testid="about-cta-contact"
          >
            Contact the studio
          </Link>
        </div>
      </section>
    </div>
  );
}

function Block({ title, body }) {
  return (
    <div className="border-t border-border pt-4">
      <h3 className="serif text-xl mb-2">{title}</h3>
      <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed">{body}</p>
    </div>
  );
}
